<div class="manual-graph-matches-container">
  <h2>Graph API Matches manuell anwenden</h2>
  <p class="help-text">
    Diese Komponente zeigt alle Matches, die nicht automatisch zugeordnet werden können (kein Match oder mehrere
    mögliche Matches). Sie können für jeden Eintrag manuell einen Azure User auswählen und den Match anwenden.
  </p>

  <!-- Matching-Optionen -->
  <div class="options-container">
    <mat-form-field
      appearance="outline"
      class="option-field">
      <mat-label>Namens-Matching Strategie</mat-label>
      <mat-select [(ngModel)]="graphMatchOptions.nameMatchingStrategy">
        <mat-option [value]="0">Normalisiert (Standard)</mat-option>
        <mat-option [value]="1">Exakt</mat-option>
        <mat-option [value]="2">Vor- und Nachname</mat-option>
      </mat-select>
      <mat-hint>Wie sollen Namen verglichen werden?</mat-hint>
    </mat-form-field>

    <mat-form-field
      appearance="outline"
      class="option-field">
      <mat-label>Email Domain Filter (optional)</mat-label>
      <input
        matInput
        placeholder="z.B. autoneum.com"
        [(ngModel)]="graphMatchOptions.emailDomainFilter" />
      <mat-hint>Nur Azure Users mit dieser Domain berücksichtigen</mat-hint>
    </mat-form-field>
  </div>

  <!-- Filter und Suche -->
  <div class="filters-container">
    <mat-form-field
      appearance="outline"
      class="filter-field">
      <mat-label>Filter nach MatchCount</mat-label>
      <mat-select
        [(ngModel)]="filterMatchCount"
        (selectionChange)="filterMatches()">
        <mat-option value="all">Alle</mat-option>
        <mat-option value="0">Kein Match (0)</mat-option>
        <mat-option value="2+">Mehrere Matches (2+)</mat-option>
      </mat-select>
    </mat-form-field>

    <mat-form-field
      appearance="outline"
      class="search-field">
      <mat-label>Suche</mat-label>
      <input
        matInput
        placeholder="Nach Invalid User ID oder Display Name suchen..."
        [(ngModel)]="searchTerm"
        (input)="filterMatches()" />
      <mat-icon matPrefix>search</mat-icon>
    </mat-form-field>

    <button
      mat-raised-button
      color="primary"
      (click)="loadMatches()"
      [disabled]="isLoading">
      <mat-icon>refresh</mat-icon>
      Aktualisieren
    </button>
  </div>

  <!-- Loading State -->
  @if (isLoading) {
  <div class="loading-container">
    <mat-spinner diameter="50"></mat-spinner>
    <p>Lade Matches...</p>
  </div>
  }

  <!-- Initial State -->
  @if (!isLoading && !hasLoadedOnce) {
  <div class="initial-state">
    <mat-icon>info</mat-icon>
    <p>Klicken Sie auf "Aktualisieren", um die Matches zu laden.</p>
  </div>
  }

  <!-- Tabelle -->
  @if (!isLoading && hasLoadedOnce) {
  <div class="table-container">
    @if (filteredMatches.length === 0) {
    <div class="no-matches">
      <mat-icon>info</mat-icon>
      <p>Keine Matches gefunden</p>
    </div>
    } @else {
    <table
      mat-table
      [dataSource]="filteredMatches"
      class="matches-table">
      <!-- Invalid User ID Spalte -->
      <ng-container matColumnDef="invalidUserId">
        <th
          mat-header-cell
          *matHeaderCellDef>
          Invalid User ID
        </th>
        <td
          mat-cell
          *matCellDef="let match"
          class="invalid-user-id">
          {{ match.invalidUserId }}
        </td>
      </ng-container>

      <!-- Extracted Display Name Spalte -->
      <ng-container matColumnDef="extractedDisplayName">
        <th
          mat-header-cell
          *matHeaderCellDef>
          Extrahierter Display-Name
        </th>
        <td
          mat-cell
          *matCellDef="let match">
          {{ match.extractedDisplayName }}
        </td>
      </ng-container>

      <!-- Match Count Spalte -->
      <ng-container matColumnDef="matchCount">
        <th
          mat-header-cell
          *matHeaderCellDef>
          Match Count
        </th>
        <td
          mat-cell
          *matCellDef="let match">
          @if (match.matchCount === 0) {
          <mat-chip color="warn">0</mat-chip>
          } @else {
          <mat-chip color="accent">{{ match.matchCount }}</mat-chip>
          }
        </td>
      </ng-container>

      <!-- Recommendation Count Spalte -->
      <ng-container matColumnDef="recommendationCount">
        <th
          mat-header-cell
          *matHeaderCellDef>
          Recommendations
        </th>
        <td
          mat-cell
          *matCellDef="let match">
          {{ match.recommendationCount }}
        </td>
      </ng-container>

      <!-- Audit Count Spalte -->
      <ng-container matColumnDef="auditCount">
        <th
          mat-header-cell
          *matHeaderCellDef>
          Audits
        </th>
        <td
          mat-cell
          *matCellDef="let match">
          {{ match.auditCount }}
        </td>
      </ng-container>

      <!-- Total Count Spalte -->
      <ng-container matColumnDef="totalCount">
        <th
          mat-header-cell
          *matHeaderCellDef>
          Gesamt
        </th>
        <td
          mat-cell
          *matCellDef="let match"
          class="total-count">
          {{ match.totalCount }}
        </td>
      </ng-container>

      <!-- Actions Spalte -->
      <ng-container matColumnDef="actions">
        <th
          mat-header-cell
          *matHeaderCellDef>
          Aktionen
        </th>
        <td
          mat-cell
          *matCellDef="let match"
          class="actions-cell">
          @if (isApplied(match)) {
          <div class="applied-status">
            <mat-icon color="primary">check_circle</mat-icon>
            <span>Angewendet</span>
          </div>
          } @else if (isApplying(match)) {
          <div class="applying-status">
            <mat-spinner diameter="20"></mat-spinner>
            <span>Wird angewendet...</span>
          </div>
          } @else {
          <div class="action-buttons">
            @if (match.matchCount === 0) {
            <!-- Kein Match gefunden: User suchen -->
            <button
              mat-raised-button
              color="primary"
              (click)="openUserSearch(match)"
              matTooltip="Azure User suchen">
              <mat-icon>search</mat-icon>
              Azure User suchen
            </button>
            @if (match.selectedAzureUserId) {
            <div class="selected-user">
              <mat-icon>person</mat-icon>
              <span>{{ getSelectedUserName(match.selectedAzureUserId) }}</span>
            </div>
            <button
              mat-raised-button
              color="accent"
              (click)="applyMatch(match)"
              [disabled]="!canApply(match)">
              <mat-icon>check</mat-icon>
              Anwenden
            </button>
            } } @else if (match.matchCount >= 2) {
            <!-- Mehrere Matches: Auswahl -->
            <div class="multiple-matches">
              <mat-form-field
                appearance="outline"
                class="match-select">
                <mat-label>Match auswählen</mat-label>
                <mat-select [(ngModel)]="match.selectedAzureUserId">
                  <mat-option [value]="undefined">-- Bitte wählen --</mat-option>
                  @for (possibleMatch of match.possibleMatches; track possibleMatch.id) {
                  <mat-option [value]="possibleMatch.id">
                    {{ possibleMatch.displayName }}
                    @if (possibleMatch.mail) { ({{ possibleMatch.mail }}) }
                  </mat-option>
                  }
                </mat-select>
              </mat-form-field>
              <button
                mat-raised-button
                color="accent"
                (click)="applyMatch(match)"
                [disabled]="!canApply(match)">
                <mat-icon>check</mat-icon>
                Anwenden
              </button>
            </div>
            }
          </div>
          }
        </td>
      </ng-container>

      <tr
        mat-header-row
        *matHeaderRowDef="displayedColumns"></tr>
      <tr
        mat-row
        *matRowDef="let row; columns: displayedColumns"
        [class.applied-row]="isApplied(row)"></tr>
    </table>
    }
  </div>
  }
</div>
